// src/client/request.ts
import { canonicalize } from "@lb-lib/canonicalize";
import { internalServerError, isServiceError, ServiceError } from "@lb-lib/error";
import { serialize } from "@lb-lib/serialize";
import * as Sentry from "@sentry/browser";

// src/client/shared/requester.ts
import { generatePlainId } from "@lb-lib/id";
import { memo } from "@lb-lib/memo";
var index = 0;
var rootId = memo(() => generatePlainId(10));
var generateRequestId = () => {
  let id = `call_${rootId()}_${index}`;
  index += 1;
  return id;
};

// src/client/request.ts
var isServer = typeof window === "undefined";
var calls = {};
var performRequest = (call) => {
  let key = `${canonicalize(call.headers)}${canonicalize(call.query)}${call.endpoint}:${call.client.name}:${call.client.version}`;
  if (!calls[key])
    calls[key] = { calls: [], to: null };
  let current = calls[key];
  let promise = new Promise((resolve, reject) => {
    current.calls.push({ call, resolve, reject });
  });
  if (current.to)
    clearTimeout(current.to);
  current.to = setTimeout(
    () => {
      let c = calls[key].calls;
      calls[key].calls = [];
      calls[key].to = null;
      let url = new URL(call.endpoint);
      url.search = new URLSearchParams(call.query).toString();
      fetch(url.toString(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...c[0].call.headers
        },
        body: JSON.stringify({
          client: call.client,
          calls: c.map((x) => ({
            id: x.call.id,
            name: x.call.name,
            payload: x.call.payload
          })).sort((a, b) => a.name.localeCompare(b.name))
        }),
        credentials: "include"
      }).then(async (res) => ({
        res: serialize.decode(
          await res.json()
        ),
        headers: res.headers
      })).then(({ res, headers }) => {
        if (res.__typename == "error") {
          let err = ServiceError.fromResponse(res);
          c.forEach((x) => x.reject(err));
          return;
        }
        for (let call2 of c) {
          let callRes = res.calls.find((x) => x.id == call2.call.id);
          if (!callRes) {
            let err2 = new ServiceError(
              internalServerError({ message: "Call not returned" })
            );
            call2.reject(err2);
            return;
          }
          if (callRes.status >= 200 && callRes.status < 300) {
            call2.resolve({
              // data: O;
              // status: number;
              // headers: Record<string, string>;
              data: callRes.result,
              status: callRes.status,
              headers
            });
          }
          let err = ServiceError.fromResponse(callRes.result);
          call2.reject(err);
        }
      }).catch((e) => {
        c.forEach(
          (x) => x.reject(
            new ServiceError(internalServerError({ message: "Unable to reach server" }))
          )
        );
      });
    },
    isServer ? 0 : 10
  );
  return promise;
};
var requesterInternal = async (call) => {
  let id = generateRequestId();
  console.log(`[call:${call.name.replace(":", "-")}:${id}] Queued`, call);
  let tries = 0;
  let error = null;
  for (let header in call.headers) {
    if (call.headers[header] === void 0)
      delete call.headers[header];
  }
  if (call.query) {
    for (let query in call.query) {
      if (call.query[query] === void 0)
        delete call.query[query];
    }
  }
  while (tries < 3) {
    try {
      return await performRequest({
        ...call,
        id
      }).then(
        (res) => {
          if (!isServer) {
            console.log(`[call:${call.name.replace(":", "-")}:${id}] Success`, res);
          }
          return res;
        },
        (err) => {
          if (isServer) {
            console.log(`[call:${call.name.replace(":", "-")}:${id}] Queued`, call);
          }
          console.log(`[call:${call.name.replace(":", "-")}:${id}] Error`, err);
          throw err;
        }
      );
    } catch (e) {
      error = e;
      if (isServiceError(e)) {
        if (e.data.status < 500)
          throw e;
      }
    }
    tries += 1;
    await new Promise((r) => setTimeout(r, tries * 1e3));
  }
  if (error)
    throw error;
  throw new ServiceError(internalServerError({ message: "Unable to reach server" }));
};
var request = async (call) => {
  try {
    return await requesterInternal(call);
  } catch (e) {
    Sentry.captureException(e);
    throw e;
  }
};

// src/client/shared/clientBuilder.ts
import { proxy } from "@lb-lib/proxy";
var noopWithContext = (cb) => cb({});
var clientBuilder = (request2, withContext = noopWithContext) => (clientOpts) => proxy(
  async (path, data, requestOpts) => await withContext(async (context) => {
    var _a;
    let headers = {
      ...clientOpts.headers,
      ...await ((_a = clientOpts.getHeaders) == null ? void 0 : _a.call(clientOpts)),
      ...requestOpts == null ? void 0 : requestOpts.headers
    };
    if (path[path.length - 1] == "getFull") {
      return await request2({
        client: clientOpts.client,
        endpoint: clientOpts.endpoint,
        payload: data,
        name: path.slice(0, -1).join(":"),
        headers,
        query: requestOpts == null ? void 0 : requestOpts.query,
        context
      });
    }
    return (await request2({
      client: clientOpts.client,
      endpoint: clientOpts.endpoint,
      payload: data,
      name: path.join(":"),
      headers,
      query: requestOpts == null ? void 0 : requestOpts.query,
      context
    })).data;
  })
);

// src/client/index.ts
var createClient = clientBuilder(request);
export {
  createClient
};
//# sourceMappingURL=index.mjs.map